import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import { TResponseModel } from "../../../../types/config";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { TStator } from "../../../../types";
import {
  AddEditStatorUsed,
  GetSpecificStatorUsed,
} from "../../../../utils/api.constant";
import "../../../../index.css";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function StatorUsedForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  const defaultValues: Partial<TStator> = {
    id: 0,
    statorUsed: "",
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchSpecificStator();
    };

    fetchData();
  }, []);

  const onFinish = async (values: TStator) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditStatorUsed, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecificStator = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(
          GetSpecificStatorUsed,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            form.setFieldsValue({ ...data });
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.leftBottom,
              content: description,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <Row
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                {`${props.id ? "Edit" : "Add"} a Stator`}
              </h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="statorUsed"
                label="Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter name"),
                  },
                ]}
              >
                <Input
                  maxLength={100}
                  size="large"
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter name"
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              size="large"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              loading={isLoading}
              className="btnDimensions"
            >
              {props.id ? "Update" : "Save"}
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
